package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.RosterPublishing
import com.evoapps.evoshift.domain.model.ConflictMetadata
import com.evoapps.evoshift.domain.model.ConflictType
import com.evoapps.evoshift.domain.repository.AvailabilityRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.StaffCheck
import com.evoapps.evoshift.domain.model.StaffChecks
import com.evoapps.evoshift.domain.model.StaffReadiness
import com.evoapps.evoshift.domain.repository.StaffRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.auth.CallerCapabilities
import com.evoapps.evoshift.domain.usecase.staff.systemToday
import kotlinx.datetime.LocalDate
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope

/**
 * Assigns an employee to a shift with soft validation: a day marked UNAVAILABLE or another shift
 * overlapping in time (in any workplace, incl. other owners' - checked by the findShiftConflicts
 * Cloud Function) is reported as a conflict the manager may force; a forced double booking is
 * recorded in the shift's conflict metadata.
 *
 * SH-47: a medical exam or OHS training expired by the shift's day is checked first: a warning
 * the manager may override ("assign anyway"), or a refusal nobody can force when the workplace
 * says so (WorkplaceSettings.blockExpiredStaffChecks). Exact for those who read the dates
 * (owners, financial managers); operational managers go by the status ("expired" today).
 */
class AssignUserToShiftUseCase(
    private val shiftRepository: ShiftRepository,
    private val availabilityRepository: AvailabilityRepository,
    private val userRepository: UserRepository,
    private val rosterRepository: RosterRepository,
    private val calculateShiftCostUseCase: CalculateShiftCostUseCase,
    /** SH-47 staff checks; without it nobody's exam or training is checked. */
    private val staffRepository: StaffRepository? = null,
    /** The workplace's "block expired" setting; without it expired checks only warn. */
    private val workplaceRepository: WorkplaceRepository? = null,
    /** Whether the caller reads the dates (MANAGE_PAY) or only the statuses; unknown: tries the dates. */
    private val caller: CallerCapabilities? = null,
    private val today: () -> LocalDate = systemToday
) {
    sealed class AssignmentResult {
        data object Success : AssignmentResult()
        /**
         * Facts about the conflict; the UI phrases them in the user's language.
         * [workplaceName] is null for another owner's workplace (it stays private).
         * [conflictStart]/[conflictEnd] are "HH:mm" of the overlapping shift.
         */
        data class ConflictFound(
            val conflictType: ConflictType,
            val employeeName: String? = null,
            val conflictingShiftId: String? = null,
            val workplaceName: String? = null,
            val isSameOwner: Boolean = false,
            val conflictStart: String? = null,
            val conflictEnd: String? = null,
            /** [ConflictType.EXPIRED_STAFF_CHECK]: which checks have run out by the shift's day. */
            val expiredChecks: Set<StaffCheck> = emptySet(),
            /** The workplace refuses this assignment: no "assign anyway" (forcing does not help). */
            val blocked: Boolean = false
        ) : AssignmentResult()
    }

    /**
     * SH-267: [borrowedFrom] is the other workplace of the group [userId] is borrowed from (their
     * medical exam and OHS dates are their home workplace's, not checked here).
     */
    suspend operator fun invoke(shiftId: String, userId: String, force: Boolean = false, borrowedFrom: String? = null): Result<AssignmentResult> = runCatching {
        val shift = shiftRepository.getShift(shiftId).getOrThrow()
        // The checks read side by side (each is a round trip; one after another made saving slow).
        val (roster, checks) = coroutineScope {
            val overlap = async { shiftRepository.findOverlappingShifts(userId, shift) }
            val roster = rosterRepository.getRoster(shift.rosterId).getOrThrow()
            val readiness = async { if (borrowedFrom == null) readinessOf(userId, roster.workplaceId) else null }
            val availability = async { availabilityRepository.getAvailability(userId, roster.workplaceId, shift.date.take(7)).getOrNull() }
            roster to Triple(readiness.await(), availability.await(), overlap.await())
        }
        val (readiness, availability, overlap) = checks

        // 0. Medical exam and OHS training (SH-47): a block holds even when forced.
        readiness?.expiredOn(LocalDate.parse(shift.date))?.takeIf { it.isNotEmpty() }?.let { expired ->
            val blocks = workplaceRepository?.getWorkplace(roster.workplaceId)?.getOrNull()?.settings?.blockExpiredStaffChecks == true
            if (blocks || !force) {
                return@runCatching AssignmentResult.ConflictFound(
                    conflictType = ConflictType.EXPIRED_STAFF_CHECK,
                    employeeName = userRepository.getUserProfile(userId).getOrNull()?.displayName,
                    expiredChecks = expired,
                    blocked = blocks
                )
            }
        }

        // 1. Availability for that day.
        val dayOfMonth = shift.date.substringAfterLast("-").toInt()
        val dayPreference = availability?.days?.get(dayOfMonth)
        if (dayPreference?.status == AvailabilityStatus.UNAVAILABLE && !force) {
            return@runCatching AssignmentResult.ConflictFound(ConflictType.UNAVAILABLE)
        }

        // 2. Another shift overlapping in time, anywhere the person works (checked server-side).
        val conflicting = overlap.getOrThrow().firstOrNull()

        if (conflicting != null && !force) {
            return@runCatching AssignmentResult.ConflictFound(
                employeeName = userRepository.getUserProfile(userId).getOrNull()?.displayName,
                conflictStart = conflicting.plannedTime.start.substringAfter('T').take(5),
                conflictEnd = conflicting.plannedTime.end.substringAfter('T').take(5),
                conflictType = ConflictType.DOUBLE_BOOKED,
                conflictingShiftId = conflicting.shiftId,
                workplaceName = conflicting.workplaceName,
                isSameOwner = conflicting.sameOwner
            )
        }

        val conflictMeta = conflicting?.let { ConflictMetadata(isForced = true, conflictType = ConflictType.DOUBLE_BOOKED, conflictingShiftId = it.shiftId) }
        shiftRepository.assignUser(shiftId, userId, conflictMeta, borrowedFrom).getOrThrow()
        // A manager filled an open shift: take its offer off the marketplace.
        if (shift.isOpen && shift.swapRequest != null) shiftRepository.updateSwapRequest(shiftId, null).getOrThrow()
        calculateShiftCostUseCase(shiftId) // best effort: the rate may not exist yet
        // SH-271: a published shift given to someone else waits for the next publish, which tells both.
        if (shift.isPublished && shift.userId != userId) {
            shiftRepository.markReassigned(shiftId, RosterPublishing.publishedAssignee(shift, userId))
                .onFailure { println("Warning: reassignment of $shiftId not marked for publishing: ${it.message}") }
        }
        AssignmentResult.Success
    }

    /** The person's checks: from the dates where the caller reads them, else from the statuses. */
    private suspend fun readinessOf(userId: String, workplaceId: String): StaffReadiness? {
        val staff = staffRepository ?: return null
        val readsDates = caller?.can(workplaceId, Capability.MANAGE_PAY) ?: true
        if (readsDates) {
            staff.getStaffRecord(userId, workplaceId).getOrNull()?.let { record ->
                return StaffChecks.readiness(record, userId, workplaceId, today())
            }
        }
        return staff.getReadinessOf(userId, workplaceId).getOrNull()
    }
}
